
import TextBox from "../../../shared/textBox";
import SelectBox from "../../../shared/selectBox";
import DateInput from "../../../shared/dateInput";
import ValuedTextBox from "../../../shared/valuedTextBox";
import { conditionalDisabledFieldsIntakeEdit } from "../../../utils/fieldConstants";
import React, { useEffect, useState } from "react";
import { formOptions } from "../../../utils/Data";
interface IntakeFormInputsProps {
  formData: {
    [key: string]: string | number | Date | FileList | null;
  };
  handleChange: (value: any, key: string) => void;
  formattedProjectId: any;
  errors: { [key: string]: string | null };
  emailErrors: { [key: string]: string | null };
  inputErrors: { [key: string]: string | null };
  onFocus: (key: string) => void; // Add focus handler
  onBlur: (key: string) => void;
  mode: any;
  localProjectData: {
    project_id: any;
    project_name: any;
    project_brief: any;
    project_lead_name: any;
    project_lead_email: any;
    business_stack_req_group: any;
    sector: any;
    consulting_type: any;
    estimated_project_start_date: any;
    estimated_project_end_date: any;
    consulting_company: any;
    final_total_cost: any;
    rate_value_received: any;
    core_deliverables: any;
  };
}
const IntakeFormInputs: React.FC<IntakeFormInputsProps> = ({
  formData,
  mode,
  errors,
  emailErrors,
  inputErrors,
  formattedProjectId,
  handleChange,
  onFocus,
  onBlur,
  localProjectData,
}) => {
  const isFieldDisabled = (fieldName: string, mode: string): boolean => {
    // In edit mode, disable fields listed in conditionalDisabledFieldsEdit
    if (
      mode === "edit" &&
      conditionalDisabledFieldsIntakeEdit.includes(fieldName)
    ) {
      return true;
    }

    return false; // Fields are enabled by default if they are not in the above lists
  };
  const [localServiceCost, setLocalServiceCost] = useState(
    formData.final_total_cost || ""
  );
  // Handle input change for service cost
  const handleServiceCostChange = (value: any) => {
    // Remove any non-digit characters
    let numericValue = value.replace(/\D/g, "");
    // Remove leading zeros
    numericValue = numericValue.replace(/^0+/, "");
    // Only update the parent state if the value changes
    if (numericValue !== formData.finalCostUSD) {
      handleChange(numericValue, "final_total_cost");
    }
  };

  const convertDateFormat = (dateString: any) => {
    if (!dateString) {
      return ""; // Return an empty string or handle as needed
    }

    // If the format is dd-mm-yyyy, convert it to yyyy-mm-dd
    const parts = dateString.split("-");

    if (parts.length === 3 && parts[0].length === 4) {
      // If it's already in yyyy-mm-dd format
      return dateString;
    }

    // If the date is in dd-mm-yyyy format, convert it
    if (parts.length === 3 && parts[0].length <= 2) {
      const [day, month, year] = dateString.split("-"); // Assuming dd-mm-yyyy
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    // If the date is in yyyy/mm/dd format, convert to yyyy-mm-dd
    const dateParts = dateString.split("/");
    if (dateParts.length === 3) {
      const [year, month, day] = dateParts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    return ""; // Return empty if invalid format
  };

  const formatDateForInput = (dateString: any) => {
    if (!dateString) return ""; // Handle case where date is empty

    if (dateString.includes("T")) {
      const [date] = dateString.split("T");
      return date;
    }
    // Check if the date is already in the required yyyy-mm-dd format
    const parts = dateString.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return dateString; // Return as is if it's already in yyyy-mm-dd format
    }

    // Convert any other date format (like dd-mm-yyyy) to yyyy-mm-dd
    const [day, month, year] = dateString.split("/"); // Assuming dd-mm-yyyy or yyyy/mm/dd format
    if (year && month && day) {
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    return ""; // Return empty if invalid format
  };
  const adjustDate = (date: any, days: number): string => {
    const parsedDate = new Date(date);
    parsedDate.setDate(parsedDate.getDate() + days);

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();

    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const formattedValue = formData.final_total_cost
      ? formData.final_total_cost
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : "";
    setLocalServiceCost(formattedValue);
  }, [formData.final_total_cost]);
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <div className="form-container">
          {/* historical form */}
          {mode === "edit" && (
            <ValuedTextBox
              label={"Project ID"}
              value={
                localProjectData.project_id || formData.project_id
                  ? formData.project_id
                  : formattedProjectId
              }
              disabled={isFieldDisabled("project_id", mode)}
            />
          )}

          <TextBox
            label="Project Name"
            value={localProjectData.project_name || formData.project_name}
            onChange={(value) => handleChange(value, "project_name")}
            disabled={isFieldDisabled("project_name", mode)}
            error={
              errors.project_name
                ? errors.project_name
                : inputErrors.project_name
                ? inputErrors.project_name
                : emailErrors.project_name
                ? emailErrors.project_name
                : null
            }
            multiline={false}
            maxLength={100}
            required={true}
            rows={1}
            onFocus={() => onFocus("project_name")}
            onBlur={() => onBlur("project_name")}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <TextBox
            label="Project Description"
            value={localProjectData.project_brief || formData.project_brief}
            onChange={(value) => handleChange(value, "project_brief")}
            disabled={isFieldDisabled("project_brief", mode)}
            error={
              inputErrors.project_brief
                ? inputErrors.project_brief
                : emailErrors.project_brief
                ? emailErrors.project_brief
                : null
            }
            multiline={true}
            maxLength={5000}
            required={false}
            rows={2}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <TextBox
            label="Project Lead Name"
            value={
              localProjectData.project_lead_name || formData.project_lead_name
            }
            onChange={(value) => handleChange(value, "project_lead_name")}
            disabled={isFieldDisabled("project_lead_name", mode)}
            error={
              errors.project_lead_name
                ? errors.project_lead_name
                : inputErrors.project_lead_name
                ? inputErrors.project_lead_name
                : null
            }
            multiline={false}
            maxLength={30}
            required={true}
            rows={1}
            onFocus={() => onFocus("project_lead_name")}
            onBlur={() => onBlur("project_lead_name")}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <TextBox
            label="Project Lead Email"
            value={
              localProjectData.project_lead_email || formData.project_lead_email
            }
            onChange={(value) => handleChange(value, "project_lead_email")}
            error={
              errors.project_lead_email
                ? errors.project_lead_email
                : inputErrors.project_lead_email
                ? inputErrors.project_lead_email
                : emailErrors.project_lead_email
                ? emailErrors.project_lead_email
                : null
            }
            multiline={false}
            maxLength={50}
            required={true}
            rows={1}
            disabled={isFieldDisabled("project_lead_email", mode)}
            onFocus={() => onFocus("project_lead_email")}
            onBlur={() => onBlur("project_lead_email")}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <SelectBox
            label="Business Stakeholder Requesting Group"
            value={
              localProjectData.business_stack_req_group ||
              formData.business_stack_req_group ||
              ""
            }
            onChange={(value) =>
              handleChange(value, "business_stack_req_group")
            }
            onFocus={() => onFocus("business_stack_req_group")}
            onBlur={() => onBlur("business_stack_req_group")}
            disabled={isFieldDisabled("business_stack_req_group", mode)}
            required={true}
            options={formOptions.stakeholderGroups}
            error={
              errors.business_stack_req_group
                ? errors.business_stack_req_group
                : null
            }
          />

          <SelectBox
            label="Sector"
            value={localProjectData.sector || formData.sector || ""}
            onChange={(value) => handleChange(value, "sector")}
            onFocus={() => onFocus("sector")}
            onBlur={() => onBlur("sector")}
            disabled={isFieldDisabled("sector", mode)}
            required={true}
            options={formOptions.sectors}
            error={errors.sector ? errors.sector : null}
          />

          <SelectBox
            label="Consulting Type"
            value={
              localProjectData.consulting_type || formData.consulting_type || ""
            }
            onChange={(value) => handleChange(value, "consulting_type")}
            onFocus={() => onFocus("consulting_type")}
            onBlur={() => onBlur("consulting_type")}
            disabled={isFieldDisabled("consulting_type", mode)}
            required={true}
            options={formOptions.consultingTypes}
            error={errors.consulting_type ? errors.consulting_type : null}
          />

          <DateInput
            label="Project Start Date"
            value={formatDateForInput(
              localProjectData.estimated_project_start_date ||
                formData.estimated_project_start_date ||
                ""
            )}
            onChange={(value) => {
              const formattedDate = convertDateFormat(value);
              handleChange(formattedDate, "estimated_project_start_date");
            }}
            max={
              adjustDate(formData.estimated_project_end_date, -1) || undefined
            }
            disabled={isFieldDisabled("estimated_project_start_date", mode)}
            error={
              inputErrors.estimated_project_start_date
                ? inputErrors.estimated_project_start_date
                : null
            }
          />

          <DateInput
            label="Project End Date"
            value={formatDateForInput(
              localProjectData.estimated_project_end_date ||
                formData.estimated_project_end_date ||
                ""
            )}
            onChange={(value) => {
              const formattedDate = convertDateFormat(value);
              handleChange(formattedDate, "estimated_project_end_date");
            }}
            min={
              adjustDate(formData.estimated_project_start_date, 1) || undefined
            }
            disabled={isFieldDisabled("estimated_project_end_date", mode)}
            error={
              inputErrors.estimated_project_end_date
                ? inputErrors.estimated_project_end_date
                : null
            }
          />

          <TextBox
            label="Which vendor company delivered this consulting work?"
            value={
              localProjectData.consulting_company || formData.consulting_company
            }
            onChange={(value) => handleChange(value, "consulting_company")}
            error={
              errors.consulting_company
                ? errors.consulting_company
                : inputErrors.consulting_company
                ? inputErrors.consulting_company
                : emailErrors.consulting_company
                ? emailErrors.consulting_company
                : null
            }
            multiline={true}
            maxLength={50}
            required={true}
            rows={2}
            disabled={isFieldDisabled("consulting_company", mode)}
            onFocus={() => onFocus("consulting_company")}
            onBlur={() => onBlur("consulting_company")}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <TextBox
            label="What were the core deliverables of the project?"
            value={
              localProjectData.core_deliverables || formData.core_deliverables
            }
            onChange={(value) => handleChange(value, "core_deliverables")}
            error={
              inputErrors.core_deliverables
                ? inputErrors.core_deliverables
                : emailErrors.core_deliverables
                ? emailErrors.core_deliverables
                : null
            }
            multiline={true}
            maxLength={5000}
            required={false}
            rows={2}
            disabled={isFieldDisabled("core_deliverables", mode)}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <TextBox
            label="What was the final total cost (USD) for this project?"
            value={localProjectData.final_total_cost || localServiceCost}
            onChange={(value) => handleServiceCostChange(value)}
            multiline={false}
            maxLength={30}
            required={false}
            rows={1}
            isCost={true}
            disabled={isFieldDisabled("final_total_cost", mode)}
            inputPropsStyle={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: '"Inter", sans-serif',
            }}
          />

          <SelectBox
            label="Please rate the value received ?"
            value={
              localProjectData.rate_value_received ||
              formData.rate_value_received ||
              ""
            }
            onChange={(value) => handleChange(value, "rate_value_received")}
            disabled={isFieldDisabled("rate_value_received", mode)}
            required={false}
            options={formOptions.rating}
          />

          {/* Display Uploaded Files */}
          <div className="form-field"></div>
        </div>
      </div>
    </>
  );
};
export default IntakeFormInputs;
